import * as React from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { CSSObject, styled, Theme, useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";
import isEmpty from "lodash/isEmpty";
import MainListItems, {
  ContainerItem,
  menuData,
  ADMIN,
  SENDER,
} from "./ListItemsLazy";
import { HermesLogo } from "./components/HermesLogo";
import { AnimatedRoute } from "./router/AnimatedRoute";
import { api } from "./api/api";
import SignIn from "./screens/SignIn";
import { useSnackbar, VariantType } from "notistack";
import DisclaimerBanner from "./components/DisclaimerBanner/DisclaimerBanner";

import { IconContainer } from "@epam/uui-components";
import { ReactComponent as DotsLoaderIcon } from "@epam/assets/icons/loaders/dots-loader.svg";
import SearchField from "./components/SearchFiels/SearchField";
import { Paths } from "./router/paths";
import { KeyboardShortcutsGuide } from "../components/composed";

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const collapsedWidth = 65; // Fixed width for collapsed drawer

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: collapsedWidth,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: typeof theme.spacing === 'function' ? theme.spacing(0, 1) : '0 8px',
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": {
      ...openedMixin(theme),
      // Ensure drawer is fixed positioned and above content
      position: 'fixed',
      zIndex: theme.zIndex.drawer,
    },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": {
      ...closedMixin(theme),
      // Ensure drawer is fixed positioned and above content
      position: 'fixed',
      zIndex: theme.zIndex.drawer,
    },
  }),
}));

const Main = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<{ open?: boolean }>(({ theme, open }) => ({
  flexGrow: 1,
  paddingTop: theme.spacing(3),
  paddingRight: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  paddingLeft: theme.spacing(3),
  minWidth: 0,
  height: '97vh',
  overflow: 'auto',
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
  }),
}));

export default function MainScreen() {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isMenuOpen, setMenuOpen] = React.useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState<string | null>("");

  const [isLoggedIn, setLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentMenuData, setCurrentMenuData] = useState<any>();
  const [user, setUser] = useState<any>({});
  const [santaState, setSantaState] = useState(0);

  const { enqueueSnackbar } = useSnackbar();

  const snackBarAddMessage = (message: string, variant: VariantType) => {
    enqueueSnackbar(message, { variant });
  };

  useEffect(() => {
    api.secretSanta
      .getState()
      .then((res) => {
        setSantaState(res.intValue);
      })
      .catch((error) => {
        handleFetchFailed(error);
      });
  }, []);

  useEffect(() => {
    if (!loading) {
      return undefined;
    }
    checkCurrentUser();
  }, [loading]);

  useEffect(() => {
    const updateMenuData = (path: string) => {
      const basePath = path.split("/")[1];
      let menu;
      if (!basePath) {
        if (user.roles.includes(ADMIN) || user.roles.includes(SENDER)) {
          menu = menuData.find((menuItem) => menuItem.name === "send");
          navigate(Paths.Send);
        } else {
          menu = menuData.find((menuItem) => menuItem.name === "couriers");
          navigate(Paths.Courier);
        }
      } else {
        menu = menuData.find((menuItem) => menuItem.name === basePath);
      }

      if (menu) {
        setCurrentMenuData(menu);
      } else {
        setCurrentMenuData({ name: "notFound" });
      }
    };

    if (!isEmpty(user)) {
      updateMenuData(pathname);
    }
  }, [pathname, user, navigate]);

  const handleDrawerClick = () => {
    setMenuOpen(!isMenuOpen);
  };

  const handleOnMenuClick = (menu: any) => {
    setCurrentMenuData(menu);
    navigate(`/${menu.name}`);
  };

  const handleLogin = (username: string, password: string) => {
    api.login
      .login(username, password)
      .then(() => {
        (async () => {
          checkCurrentUser();
        })();
      })
      .catch((error) => handleFetchFailed(error));
  };

  const handleLogout = () => {
    api.login
      .logout()
      .then(() => {
        window.location.reload();
      })
      .catch(() => {
        // Disregard all errors and just refresh
        window.location.reload();
      });
  };

  const handleFetchFailed = (error: any) => {
    if (error) {
      if (error.status === 401) {
        setLoggedIn(false);
      } else {
        snackBarAddMessage(getErrorMessage(error.status), "error");
      }
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode: number) => {
    switch (errorCode) {
      case 403:
        return "You don't have permission for this action";
      case 404:
        return "Requested object not found";
      default:
        return "Something went wrong";
    }
  };

  const checkCurrentUser = () => {
    (async () => {
      api.user
        .current()
        .then((user) => {
          setUser(user);
          setLoggedIn(true);
          setLoading(false);
        })
        .catch((error) => {
          handleFetchFailed(error);
        });
    })();
  };

  const searchClickAction = (searchStr: string) => {
    setSearchValue(searchStr);
  };

  // Keyboard shortcut: Cmd/Ctrl + K for search (future feature)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Cmd+K or Ctrl+K for search
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setShowSearch(true);
      }

      // Cmd+B or Ctrl+B to toggle sidebar
      if ((event.metaKey || event.ctrlKey) && event.key === 'b') {
        event.preventDefault();
        setMenuOpen(!isMenuOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  // Announce route changes to screen readers
  useEffect(() => {
    if (currentMenuData?.hName) {
      // Announce page navigation
      const announcement = `Navigated to ${currentMenuData.hName}`;
      const ariaLive = document.getElementById('route-announcer');
      if (ariaLive) {
        ariaLive.textContent = announcement;
      }
    }
  }, [currentMenuData]);

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        minHeight="95vh"
        alignItems="center"
      >
        <IconContainer
          icon={DotsLoaderIcon}
          style={{ fill: theme.palette.secondary.main }}
          size={70}
        />
      </Box>
    );
  if (!isLoggedIn) return <SignIn onClick={handleLogin} />;

  return (
    <Box sx={{ display: "flex" }}>
      {/* Screen reader live region for route announcements */}
      <div
        id="route-announcer"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      />

      {!(user.roles.includes(ADMIN) || user.roles.includes(SENDER)) && (
        <DisclaimerBanner />
      )}
      <Drawer
        variant="permanent"
        open={isMenuOpen}
        aria-label="Main navigation"
      >
        {/* Animated Hermes Logo */}
        <HermesLogo open={isMenuOpen} />
        <Divider />

        <List>
          <MainListItems
            open={isMenuOpen}
            onClick={handleOnMenuClick}
            currentMenu={currentMenuData}
            user={user}
            santaState={santaState}
          />
        </List>
        <Divider sx={{ flex: 1 }} />

        {/* Logout Button */}
        <List sx={{ paddingBottom: 0 }}>
          <Tooltip title={!isMenuOpen ? "Logout" : ""} placement="right">
            <ListItemButton
              component={motion.div}
              whileHover={{
                backgroundColor: theme.palette.action.hover,
                x: 4,
              }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
              onClick={handleLogout}
              sx={{
                minHeight: 48,
                justifyContent: isMenuOpen ? "initial" : "center",
                px: 2.5,
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
              aria-label="Logout"
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isMenuOpen ? 3 : "auto",
                  justifyContent: "center",
                  color: theme.palette.error.main,
                }}
              >
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  opacity: isMenuOpen ? 1 : 0,
                  transition: "opacity 0.2s ease",
                }}
                primaryTypographyProps={{
                  sx: {
                    fontWeight: 500,
                  },
                }}
              />
            </ListItemButton>
          </Tooltip>
        </List>

        <DrawerHeader>
          <IconButton
            component={motion.button}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleDrawerClick}
            aria-label={isMenuOpen ? "Collapse navigation menu" : "Expand navigation menu"}
            aria-expanded={isMenuOpen}
            sx={{
              '&:focus-visible': {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
            }}
          >
            {!isMenuOpen ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </DrawerHeader>
      </Drawer>
      <Main component="main" open={isMenuOpen}>
        {showSearch ? (
          <SearchField searchClickAction={searchClickAction} />
        ) : (
          <div />
        )}
        <AnimatedRoute>
          <ContainerItem
            current={currentMenuData}
            user={user}
            setShowSearch={setShowSearch}
            searchValue={searchValue}
            onFetchFailed={handleFetchFailed}
            snackBarAddMessage={snackBarAddMessage}
            courieId={id}
            santaState={santaState}
          />
        </AnimatedRoute>
      </Main>

      {/* Keyboard shortcuts guide */}
      <KeyboardShortcutsGuide />
    </Box>
  );
}
